import { CaptureError, normalizeCaptureError } from './CaptureError';
import { BrowserCaptureSession } from './CaptureSession';
import { browserCaptureEnvironment, detectCaptureCapabilities } from './capabilities';
import type {
  CaptureCapabilities,
  CaptureManagerState,
  CaptureSession,
  DisplayMediaProvider,
  StartCaptureOptions,
} from './types';

export interface MediaCaptureManagerOptions {
  readonly mediaDevices?: DisplayMediaProvider;
  readonly secureContext?: boolean;
  readonly now?: () => number;
  readonly createId?: () => string;
}

export class MediaCaptureManager {
  private readonly mediaDevices: DisplayMediaProvider | undefined;
  private readonly secureContext: boolean;
  private readonly now: () => number;
  private readonly createId: () => string;
  private readonly listeners = new Set<(state: CaptureManagerState) => void>();
  private currentState: CaptureManagerState = { status: 'idle' };
  private pending: Promise<CaptureSession> | undefined;
  private requestEpoch = 0;

  constructor(options: MediaCaptureManagerOptions = {}) {
    const browser = browserCaptureEnvironment();
    this.mediaDevices = options.mediaDevices ?? browser.mediaDevices;
    this.secureContext = options.secureContext ?? browser.secureContext;
    this.now = options.now ?? Date.now;
    this.createId = options.createId ?? (() => crypto.randomUUID());
  }

  get state(): CaptureManagerState {
    return this.currentState;
  }

  get capabilities(): CaptureCapabilities {
    return detectCaptureCapabilities({
      secureContext: this.secureContext,
      mediaDevices: this.mediaDevices,
    });
  }

  subscribe(listener: (state: CaptureManagerState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  start(options: StartCaptureOptions = {}): Promise<CaptureSession> {
    if (this.pending !== undefined) {
      return this.pending;
    }

    if (this.currentState.status === 'active') {
      return Promise.reject(new CaptureError('capture-already-active'));
    }

    const capabilities = this.capabilities;
    if (!capabilities.supported) {
      const error = new CaptureError(
        capabilities.reason === 'insecure-context' ? 'insecure-context' : 'unsupported',
      );
      this.transition({ status: 'failed', error });
      return Promise.reject(error);
    }

    if (this.mediaDevices === undefined) {
      const error = new CaptureError('unsupported');
      this.transition({ status: 'failed', error });
      return Promise.reject(error);
    }

    const requestedSharedAudio = options.sharedAudio === true;
    const epoch = ++this.requestEpoch;
    this.transition({ status: 'requesting', requestedSharedAudio });

    try {
      const streamPromise = this.mediaDevices.getDisplayMedia({
        video: true,
        audio: requestedSharedAudio,
      });
      const operation = this.resolveCapture(streamPromise, epoch, requestedSharedAudio);
      this.pending = operation;
      const clearPending = () => {
        if (this.pending === operation) {
          this.pending = undefined;
        }
      };
      void operation.then(clearPending, clearPending);
      return operation;
    } catch (error) {
      const normalized = normalizeCaptureError(error);
      this.transition({ status: 'failed', error: normalized });
      return Promise.reject(normalized);
    }
  }

  stop(): void {
    if (this.currentState.status === 'requesting') {
      this.requestEpoch += 1;
      this.transition({ status: 'idle' });
      return;
    }

    if (this.currentState.status === 'active') {
      this.currentState.session.stop();
      return;
    }

    if (this.currentState.status === 'failed') {
      this.transition({ status: 'idle' });
    }
  }

  dispose(): void {
    this.stop();
    this.listeners.clear();
  }

  private async resolveCapture(
    streamPromise: Promise<MediaStream>,
    epoch: number,
    requestedSharedAudio: boolean,
  ): Promise<CaptureSession> {
    try {
      const stream = await streamPromise;
      if (epoch !== this.requestEpoch) {
        stopAllTracks(stream);
        throw new CaptureError('capture-cancelled');
      }

      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack === undefined) {
        stopAllTracks(stream);
        throw new CaptureError('missing-video-track');
      }

      const session = new BrowserCaptureSession(
        `${this.now()}-${this.createId()}`,
        stream,
        videoTrack,
        requestedSharedAudio,
        (endedSession) => {
          if (this.currentState.status === 'active' && this.currentState.session === endedSession) {
            this.transition({ status: 'idle' });
          }
        },
      );
      this.transition({ status: 'active', session });
      return session;
    } catch (error) {
      const normalized = normalizeCaptureError(error);
      if (epoch === this.requestEpoch) {
        this.transition({ status: 'failed', error: normalized });
      }
      throw normalized;
    }
  }

  private transition(state: CaptureManagerState): void {
    this.currentState = state;
    for (const listener of this.listeners) {
      listener(state);
    }
  }
}

function stopAllTracks(stream: MediaStream): void {
  for (const track of stream.getTracks()) {
    try {
      track.stop();
    } catch {
      // Best-effort each track independently so one browser failure cannot leak the rest.
    }
  }
}
